import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { track } from '@amplitude/analytics-browser';
import { UserIcon } from '@nimbus-ds/icons';
import { CalloutCard } from '@nimbus-ds/patterns';
import { ROUTES } from 'App/Routes/constants';
import { useStoreInfo } from 'commons/hooks';
import { TrialPeriod } from 'commons/services/configurationService/configurationServiceResponses';
import { formatCurrency, useTranslationWithPrefix } from 'commons/utils';
import { calculateTrialPeriodProgress } from 'commons/utils/trialPeriodUtils';

export enum PendingTrialPeriodAlertPage {
  CONFIGURATION = 'configuration',
  HOME = 'home',
  LANDING = 'landing',
}

const CONFIGURATION_ALERT_LEVEL = 'sin_ventas';

interface InterfacePendingTrialPeriodAlert {
  trialPeriod: TrialPeriod;
  page: PendingTrialPeriodAlertPage;
  blockedTrialPeriod?: boolean;
}

const PendingTrialPeriodAlert = ({
  trialPeriod,
  page,
  blockedTrialPeriod,
}: InterfacePendingTrialPeriodAlert): JSX.Element => {
  const { t } = useTranslationWithPrefix('commons.trialPeriod');
  const { storeInfo } = useStoreInfo();
  const history = useHistory();

  const trialProgress = calculateTrialPeriodProgress(
    trialPeriod,
    blockedTrialPeriod,
  );

  const trackingProperties = useMemo(
    () => ({
      page,
      alert_level:
        page === PendingTrialPeriodAlertPage.CONFIGURATION
          ? CONFIGURATION_ALERT_LEVEL
          : trialProgress.alertLevel,
      criterion: trialProgress.leadingCriterion,
      blockedTrialPeriod,
    }),
    [
      page,
      trialProgress.alertLevel,
      trialProgress.leadingCriterion,
      blockedTrialPeriod,
    ],
  );

  useEffect(() => {
    const eventName = blockedTrialPeriod
      ? 'pago-nube-block-prueba-de-vida-view'
      : 'pago-nube-prueba-de-vida-view';
    track(eventName, trackingProperties);
  }, [blockedTrialPeriod, trackingProperties]);

  const redirectToValidateIdentity = () => {
    const eventName = blockedTrialPeriod
      ? 'pago-nube-block-prueba-de-vida-click'
      : 'pago-nube-prueba-de-vida-click';
    track(eventName, trackingProperties);
    history.push(ROUTES.IDENTITY_VALIDATION_STEPS);
  };

  const getTrialPeriodAppearance = (): 'primary' | 'warning' | 'danger' => {
    switch (trialProgress.alertLevel) {
      case 'initial':
        return 'primary';
      case 'warning':
        return 'warning';
      case 'danger':
      case 'willBeBlocked':
      case 'blocked':
        return 'danger';
      default:
        return 'primary';
    }
  };

  const getTrialPeriodDescription = (): string => {
    const { alertLevel, leadingCriterion, remaining } = trialProgress;
    const { thresholds } = trialPeriod;

    if (alertLevel === 'initial') {
      return t('initial.description', {
        maxDays: thresholds.maxDays,
        maxBalance: formatCurrency(thresholds.maxGeneratedBalance, storeInfo),
        maxSales: thresholds.maxSalesCount,
      });
    }

    if (alertLevel === 'warning') {
      const descriptionKey = {
        days: 'warning.descriptionDays',
        sales: 'warning.descriptionSales',
        balance: 'warning.descriptionBalance',
      }[leadingCriterion];

      return t(descriptionKey, {
        maxDays: thresholds.maxDays,
        maxSales: thresholds.maxSalesCount,
        maxBalance: formatCurrency(thresholds.maxGeneratedBalance, storeInfo),
      });
    }

    if (alertLevel === 'danger') {
      const descriptionKey = {
        days: 'danger.descriptionDays',
        sales: 'danger.descriptionSales',
        balance: 'danger.descriptionBalance',
      }[leadingCriterion];

      const getRemainingValue = () => {
        if (leadingCriterion === 'balance')
          return formatCurrency(remaining.balance, storeInfo);
        if (leadingCriterion === 'days') return remaining.days;
        return remaining.sales;
      };

      return t(descriptionKey, {
        remaining: getRemainingValue(),
      });
    }

    if (alertLevel === 'willBeBlocked') {
      return t('willBeBlocked.description');
    }

    return t('blocked.description');
  };

  if (page === PendingTrialPeriodAlertPage.CONFIGURATION) {
    return (
      <CalloutCard
        appearance="primary"
        icon={UserIcon}
        title={t(`configuration.title`)}
        subtitle={t(`configuration.description`, {
          maxDays: trialPeriod.thresholds.maxDays,
          maxBalance: formatCurrency(
            trialPeriod.thresholds.maxGeneratedBalance,
            storeInfo,
          ),
          maxSales: trialPeriod.thresholds.maxSalesCount,
        })}
        data-testid="pending-trial-period-alert"
        onClick={redirectToValidateIdentity}
      />
    );
  }

  return (
    <CalloutCard
      appearance={getTrialPeriodAppearance()}
      icon={UserIcon}
      title={t(`${trialProgress.alertLevel}.title`)}
      subtitle={getTrialPeriodDescription()}
      data-testid="pending-trial-period-alert"
      onClick={redirectToValidateIdentity}
    />
  );
};

export default PendingTrialPeriodAlert;
